<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { Plus } from 'lucide-vue-next';
import { useRouter } from 'vue-router';
// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Label } from '@/components/shadcnui/label';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Switch } from '@/components/shadcnui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useRelease } from '@/composable/useRelease';
import { useChipset } from '@/composable/useChipset';
import { useModel } from '@/composable/useModel';
import { useFw } from '@/composable/useFw';


// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadReleaseList, onPaginationRelease } = useRelease();
const { onReadChipsetList } = useChipset();
const { onReadModelList } = useModel();
const { onReadFwList } = useFw();

const router = useRouter();

// 상태 관리
const filterChipset = ref(0);
const filterModelType = ref(0);
const currentPage = ref(1);
const totalPage = ref(1);
const isLoading = ref(true);
const settingMode = ref(1);

// 마우스 위치 및 툴팁 상태
const mousePos = ref({ x: 0, y: 0 });
const hoveredDescr = ref<string | null>(null); 

// 필터링 조건 변경 시 모델 리스트 업데이트
watch([filterChipset, filterModelType], async () => {

  if (!settingMode.value) {
    UserStore.relfilter.chipset_id = filterChipset.value;
    UserStore.relfilter.model_id = filterModelType.value;
    //RSStore.relfilter.page = currentPage.value;
    console.log("필터링 조건 업데이트됨:", UserStore.relfilter);
    if (UserStore.relfilter.page !== 1) {
      currentPage.value = 1; // 페이지네이션을 첫 페이지로 리셋
      UserStore.relfilter.page = 1;
    }
    totalPage.value = await onPaginationRelease(UserStore.relfilter) ?? 1;
    await onReadReleaseList(UserStore.relfilter);
  }
});

// 행 클릭 핸들러 (상세 페이지로 이동)
const rowClickHandler = (releaseId: number) => {
  if (!UserStore.releaseEdit && !UserStore.releasenoteEdit) return;
  console.log("릴리스 ID:", releaseId);
  UserStore.relfilter.page = currentPage.value;
  router.push(`/releases/update/${releaseId}`);
};

// 새 릴리즈 버튼 클릭 핸들러
const createReleaseHandler = () => {
  router.push('/releases/new');
  currentPage.value = 1; // 페이지네이션을 첫 페이지로 리셋
};

// 페이지네이션 핸들러
const paginationHandler = async (page: number) => {
  if (page < 1) {
    page = 1;
  }
  else if (page > totalPage.value) {
    page = totalPage.value;
  }
  currentPage.value = page;
  UserStore.relfilter.page = page;
  await onReadReleaseList(UserStore.relfilter);
};

// 마우스 이동 핸들러 (툴팁 위치 업데이트)
const handleMouseMove = (event: MouseEvent) => {
  mousePos.value = { x: event.clientX, y: event.clientY };
};

const getFirmwareDisplay = (release : any) => {
  const types = []
  if (release.fwrs_full) types.push('F')
  if (release.fwrs_patch) types.push('P')
  if (release.fwrs_bridge) types.push('B')
  
  const suffix = types.join('') 

  if (!suffix) return ''

  const targetId = release.fwrs_bridge || release.fwrs_patch || release.fwrs_full
  const targetFw = RSStore.fwList.find(f => f.id === targetId)
  
  if (targetFw && targetFw.fwvername) {
    return `${targetFw.fwvername} - ${suffix}`
  } else {
    return suffix 
  }
}

// 컴포넌트 마운트 시 데이터 로드
onMounted(async () => {
  if (UserStore.relfilter.chipset_id || UserStore.relfilter.model_id) {
    filterChipset.value = UserStore.relfilter.chipset_id;
    filterModelType.value = UserStore.relfilter.model_id;
  }
  if (UserStore.relfilter.page) {
    currentPage.value = UserStore.relfilter.page;
    console.log("현제 페이지 :" + currentPage.value);
  }
  totalPage.value = await onPaginationRelease(UserStore.relfilter) ?? 1;
  await onReadChipsetList({ page: 0, size: 0 });
  await onReadModelList({ chipset_id: 0, page: 0, size: 0 });
  await onReadFwList({ model_id: 0, chipset_id: 0, fwtype_id: 0, page: 0, size: 0 });
  await onReadReleaseList(UserStore.relfilter).finally(() => {
    isLoading.value = false;
  });
  settingMode.value = 0;
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-2 text-foreground">
      <div>
        <h1 class="text-3xl font-bold">릴리즈</h1>
      </div>
      <!-- 필터 -->
      <div class="flex gap-4 mb-2">
        <div class="w-48">
          <Label class="mb-2 block">Chipset</Label>
          <Select v-model="filterChipset">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
                {{ chipset.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="w-48">
          <Label class="mb-2 block">Model</Label>
          <Select v-model="filterModelType">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="model in RSStore.modelList" :key="model.id" :value="model.id">
                {{ model.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="flex items-end">
          <Button @click="createReleaseHandler" :disabled="!UserStore.releaseAdd">
            <Plus class="mr-2 md-4 w-4" /> 신규 릴리즈
          </Button>
        </div>
      </div>
    </div>

    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50">
            <TableHead class="px-5">ID</TableHead>
            <TableHead>Chipset</TableHead>
            <TableHead>Model</TableHead>
            <TableHead class="">Title</TableHead>
            <TableHead>Firmware</TableHead>
            <TableHead>Apps</TableHead>
            <TableHead>DL Count</TableHead>
            <TableHead>DL Limit</TableHead>
            <TableHead>Pin</TableHead>
            <TableHead >Use</TableHead>
            <TableHead >Beta</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태  -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="11" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <template v-else>
            <TableRow v-for="release in RSStore.releaseList" :key="release.id"
              class="hover:bg-accent/50 cursor-pointer" @click="rowClickHandler(release.id)"
              @mousemove="handleMouseMove"
              @mouseenter="hoveredDescr = release.descr || 'No description.'"
              @mouseleave="hoveredDescr = null">
              <TableCell class="px-5 w-30">{{ release.id }}</TableCell>
              <TableCell class="max-w-40 truncate">{{RSStore.chipsetList.find(c => c.id === release.chipset_id)?.name}}
              </TableCell>
              <TableCell class="max-w-50 truncate">{{RSStore.modelList.find(m => m.id === release.model_id)?.name}}</TableCell>
              <TableCell class="max-w-60 truncate">{{ release.title }}</TableCell>
              <TableCell class="w-60">{{
                getFirmwareDisplay(release)
              }} </TableCell>
              <TableCell class="w-30">{{ (release.apprs_launcher ? 'L' : '') + (release.apprs_service ? 'S' : '') +
                (release.apprs_upgrade ? 'U' : '')
                + (release.apprs_helpcenter ? 'H' : '') + (release.apprs_tunerservice ? 'T' : '') +
                (release.apprs_networkstorage ? 'N' : '')
                + (release.apprs_mol ? 'M' : '') + (release.apprs_market ? 'R' : '') + (release.apprs_rcuapp ? 'C' :
                  '')
              }}</TableCell>
              <TableCell class="w-30">{{ release.dlcount }}</TableCell>
              <TableCell class="w-30">{{ release.dllimit }}</TableCell>
              <TableCell class="w-20"><Switch :model-value="release.pincode_id > 0" :disabled="true"/></TableCell>
              <TableCell class="w-20"><Switch :model-value="release.isuse === 1" :disabled="true"/></TableCell>
              <TableCell class="w-5"><Switch :model-value="release.isbeta === 1" :disabled="true"/></TableCell>
            </TableRow>

            <TableRow v-for="i in (UserStore.itemPerPage - RSStore.releaseList.length)" :key="'empty-' + i">
              <TableCell colspan="11">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <div class="mt-6 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalPage * UserStore.itemPerPage"
        :items-per-page="UserStore.itemPerPage" :sibling-count="3" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>

      </Pagination>
    </div>
    <!-- Description 툴팁 -->
    <Teleport to="body">
      <div v-if="hoveredDescr" :style="{
        position: 'fixed',
        left: `${mousePos.x + 5}px`,
        top: `${mousePos.y + 5}px`,
      }"
        class="z-50 pointer-events-none min-w-80 w-auto bg-popover text-popover-foreground rounded-md border p-4 shadow-md outline-none">
        <div class="space-y-1">
          <h4 class="text-sm font-semibold">Description</h4>
          <hr class=""/>
          <p class="text-sm whitespace-pre-wrap"> {{ hoveredDescr }}</p>
        </div>
      </div>
    </Teleport>
  </div>
</template>